import {
  MeasureReducer,
  ReportBuilderDataSourceId,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import { FactTables } from '../../models/fact-tables';
import { toMeasureBuilderData } from '../../models/measure-properties';
import { ReportBuilderColumns } from '../../report-builder-columns';
import { ChartFilterDefinition } from '../charts/chart-filter-definition';
import { ReportBuilderCharts } from '../charts/report-builder-charts';
import { ReportBuilderCommonCharts } from '../charts/report-builder-common-charts';
import { ReportBuilderDataSourceInstance } from '../report-builder-data-source-instance';
import { ReportBuilderGroups } from '../report-builder-groups';
import { ReportBuilderScopeDataAccessors } from '../report-builder-scope-data-accessors';

export class AppointmentsDataSource extends ReportBuilderDataSourceInstance {
  override readonly id = ReportBuilderDataSourceId.Appointments;
  override readonly name = 'Appointments';
  override readonly description =
    'One result per appointment within Principle. This includes information about the practice and patient associated with each appointment.';
  override readonly factTable = FactTables.appointmentEvent.latestEvent;
  override readonly rowItemName = 'Appointment';

  override readonly queryableTimestamps = [
    { property: this.factTable.appointment.date },
    { property: this.factTable.appointment.dateBookedOnline },
    { property: this.factTable.appointment.firstBookedAt },
    { property: this.factTable.appointment.confirmedAt },
  ];

  scopes = [TemplateScope.Appointment, TemplateScope.Patient];
  scopeDataAccessor = ReportBuilderScopeDataAccessors.build([
    this.factTable.appointment,
    this.factTable.patient,
  ]);

  override readonly groupByOptions = [
    ReportBuilderGroups.toGroup('Common', [
      ReportBuilderGroups.toOption(this.factTable.practitioner.name),
      ReportBuilderGroups.toOption(this.factTable.practice.name),
      ReportBuilderGroups.toOption(this.factTable.brand.name),
      ReportBuilderGroups.toOption(this.factTable.nextAppointmentBooked),
      ReportBuilderGroups.toOption(this.factTable.hasNextAppointment),
      ReportBuilderGroups.toOption(this.factTable.isFirstAppointment),
      ReportBuilderGroups.toOption(this.factTable.existingPatient),
      ReportBuilderGroups.toOption(this.factTable.treatments, [
        MeasureReducer.Count,
      ]),
      ReportBuilderGroups.toOption(this.factTable.serviceCodes, [
        MeasureReducer.Count,
      ]),
      ReportBuilderGroups.toOption(this.factTable.count),
    ]),
    ReportBuilderGroups.appointment(this.factTable.appointment),
    ReportBuilderGroups.invoice(this.factTable.invoice),
    ReportBuilderGroups.patient(this.factTable.patient),
  ];

  override readonly availableColumns = [
    ReportBuilderColumns.toColumnGroup('Appointment', [
      this.factTable.practitioner.name,
      this.factTable.practice.name,
      this.factTable.treatmentPlan.name,
      this.factTable.waitTime,
      this.factTable.hourlyRate,
      this.factTable.duration,
      this.factTable.treatmentCost,
      this.factTable.nextAppointmentBooked,
      this.factTable.hasNextAppointment,
      this.factTable.isFirstAppointment,
      this.factTable.treatments,
      this.factTable.serviceCodes,
      ...ReportBuilderColumns.appointment(this.factTable.appointment),
      this.factTable.link,
    ]),
    ReportBuilderColumns.toColumnGroup('Invoice', [
      ...ReportBuilderColumns.invoice(this.factTable.invoice),
      this.factTable.invoiceLink,
    ]),
    ReportBuilderColumns.toColumnGroup('Patient', [
      ...ReportBuilderColumns.patient(this.factTable.patient),
      this.factTable.patientLink,
    ]),
  ];

  override readonly defaultColumns = ReportBuilderColumns.toReferences([
    this.factTable.patient.name,
    this.factTable.appointment.date,
    this.factTable.practitioner.name,
    this.factTable.appointment.treatmentCategory,
    this.factTable.duration,
    this.factTable.treatmentCost,
    this.factTable.link,
  ]);

  override readonly availableCharts = [
    ReportBuilderCharts.predefined(
      'overview.brand.price',
      this.factTable.brand.name,
      ChartFilterDefinition.numberSummary(
        'Total Price',
        this.factTable.brand.name,
        [toMeasureBuilderData(this.factTable.treatmentCost)]
      )
    ),
    ReportBuilderCharts.predefined(
      'overview.practice',
      this.factTable.practice.name,
      ChartFilterDefinition.pie('Practice', this.factTable.practice.name, [
        toMeasureBuilderData(this.factTable.treatmentCost),
      ])
    ),
    ReportBuilderCharts.predefined(
      'overview.practitioner',
      this.factTable.practitioner.name,
      ChartFilterDefinition.row(
        `Practitioner`,
        this.factTable.practitioner.name,
        [toMeasureBuilderData(this.factTable.treatmentCost)]
      )
    ),
    ReportBuilderCharts.predefined(
      'overview.totalCost',
      this.factTable.brand.name,
      ChartFilterDefinition.numberSummary(
        'Total Treatment Amount',
        this.factTable.brand.name,
        [toMeasureBuilderData(this.factTable.treatmentCost)]
      )
    ),
    ReportBuilderCharts.predefined(
      'overview.invoice.status',
      this.factTable.invoice.status,
      ChartFilterDefinition.pie(
        'Invoice Status',
        this.factTable.invoice.status,
        [toMeasureBuilderData(this.factTable.treatmentCost)]
      )
    ),
    ...ReportBuilderCommonCharts.patient(
      this.factTable.patient,
      this.factTable.treatmentCost
    ),
    ReportBuilderCharts.predefined(
      'appointment.status',
      this.factTable.appointment.status,
      ChartFilterDefinition.pie('Status', this.factTable.appointment.status, [
        toMeasureBuilderData(this.factTable.count),
      ])
    ),
    ReportBuilderCharts.predefined(
      'appointment.treatmentCategory',
      this.factTable.appointment.treatmentCategory,
      ChartFilterDefinition.column(
        'Treatment Category',
        this.factTable.appointment.treatmentCategory,
        [toMeasureBuilderData(this.factTable.treatmentCost)],
        { rotateXAxisLabels: true },
        { groupLimit: 15, otherGroupLabel: 'Other', colourOverride: true }
      )
    ),
    ReportBuilderCharts.predefined(
      'appointment.tags',
      this.factTable.appointment.tags,
      ChartFilterDefinition.row(
        'Tags',
        this.factTable.appointment.tags,
        [toMeasureBuilderData(this.factTable.treatmentCost)],
        { groupLimit: 15, otherGroupLabel: 'Other' }
      )
    ),
    ReportBuilderCharts.predefined(
      'appointment.tags.select',
      this.factTable.appointment.tags,
      ChartFilterDefinition.selectMenu(
        'Tags',
        this.factTable.appointment.tags,
        [toMeasureBuilderData(this.factTable.treatmentCost)]
      )
    ),
    ReportBuilderCharts.predefined(
      'appointment.treatments',
      this.factTable.treatments,
      ChartFilterDefinition.selectMenu(
        'Treatments',
        this.factTable.treatments,
        [toMeasureBuilderData(this.factTable.count)]
      )
    ),
    ReportBuilderCharts.predefined(
      'appointment.serviceCodes',
      this.factTable.serviceCodes,
      ChartFilterDefinition.selectMenu(
        'Service Codes',
        this.factTable.serviceCodes,
        [toMeasureBuilderData(this.factTable.count)]
      )
    ),
    ReportBuilderCharts.predefined(
      'appointment.isFirstAppointment',
      this.factTable.isFirstAppointment,
      ChartFilterDefinition.pie(
        'Is First Appointment',
        this.factTable.isFirstAppointment,
        [
          toMeasureBuilderData(
            this.factTable.isFirstAppointment,
            MeasureReducer.Count
          ),
        ]
      )
    ),
    ReportBuilderCharts.predefined(
      'appointment.isBookedOnline',
      this.factTable.appointment.isBookedOnline,
      ChartFilterDefinition.pie(
        'Is Booked Online',
        this.factTable.appointment.isBookedOnline,
        [toMeasureBuilderData(this.factTable.count)]
      )
    ),
    ReportBuilderCharts.predefined(
      'appointment.dateBookedOnline',
      this.factTable.appointment.dateBookedOnline,
      ChartFilterDefinition.columnOverTime(
        'Date Booked Online',
        this.factTable.appointment.dateBookedOnline,
        [toMeasureBuilderData(this.factTable.treatmentCost)]
      )
    ),
    ReportBuilderCharts.predefined(
      'appointment.nextAppointmentBooked',
      this.factTable.nextAppointmentBooked,
      ChartFilterDefinition.pie(
        'Next Appointment Booked At Checkout',
        this.factTable.nextAppointmentBooked,
        [
          toMeasureBuilderData(
            this.factTable.nextAppointmentBooked,
            MeasureReducer.Count
          ),
        ]
      )
    ),
    ReportBuilderCharts.predefined(
      'appointment.hasNextAppointment',
      this.factTable.hasNextAppointment,
      ChartFilterDefinition.pie(
        'Has Next Appointment',
        this.factTable.hasNextAppointment,
        [
          toMeasureBuilderData(
            this.factTable.hasNextAppointment,
            MeasureReducer.Count
          ),
        ]
      )
    ),

    ReportBuilderCharts.predefined(
      'appointment.treatmentCost',
      this.factTable.appointment.date,
      ChartFilterDefinition.columnOverTime(
        'Treatment Cost',
        this.factTable.appointment.date,
        [toMeasureBuilderData(this.factTable.treatmentCost)]
      )
    ),
    ReportBuilderCharts.predefined(
      'appointment.averageDuration',
      this.factTable.appointment.date,
      ChartFilterDefinition.columnOverTime(
        'Average Duration',
        this.factTable.appointment.date,
        [toMeasureBuilderData(this.factTable.duration, MeasureReducer.Average)],
        { width: ReportBuilderCharts.gridSpan(2) }
      )
    ),
    ReportBuilderCharts.predefined(
      'appointment.hourlyRate',
      this.factTable.appointment.date,
      ChartFilterDefinition.columnOverTime(
        'Hourly Rate',
        this.factTable.appointment.date,
        [
          toMeasureBuilderData(
            this.factTable.hourlyRate,
            MeasureReducer.Average
          ),
        ],
        undefined
      )
    ),
    ReportBuilderCharts.predefined(
      'appointment.waitTime',
      this.factTable.appointment.date,
      ChartFilterDefinition.columnOverTime(
        'Average Wait Time',
        this.factTable.appointment.date,
        [toMeasureBuilderData(this.factTable.waitTime, MeasureReducer.Average)],
        undefined
      )
    ),
  ];
}
